import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setNavigating,
  setDirection,
  setIsLoading,
  stopTutorial,
  setCurrentStep,
} from "../../Redux/tutorialSlice";
import { useLocation, useNavigate } from "react-router-dom";
import tutorialSteps from "./tutorialSteps";
import { setWizardStep, openWizard } from "../../Redux/wizardSlice";

const useTutorialSteps = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentStep = useSelector((state) => state.tutorial.currentStep);
  const navigating = useSelector((state) => state.tutorial.navigating);
  const location = useLocation();

  const navigationLock = useRef(false);

  const maybeNavigate = (stepData) => {
    if (stepData.route !== tutorialSteps[currentStep]?.route) {
      dispatch(setNavigating(true));
      navigate(stepData.route);
    }
  };

  const syncWizardStep = (wizardStep) => {
    if (typeof wizardStep === "number") {
      dispatch(openWizard());
      dispatch(setWizardStep(wizardStep));
    }
  };

  const finalizeStep = (stepIndex) => {
    // keep direction for animations, etc.
    dispatch(setDirection(stepIndex > currentStep ? "next" : "prev"));

    // JUMP (or step) to exactly the requested index
    dispatch(setCurrentStep(stepIndex));

    dispatch(setNavigating(false));
    dispatch(setIsLoading(false));

    setTimeout(() => {
      navigationLock.current = false;
    }, 300);
  };

  const processStep = useCallback(
    async (stepIndex) => {
      if (navigationLock.current) return;
      if (stepIndex < 0 || stepIndex >= tutorialSteps.length) return;

      const stepData = tutorialSteps[stepIndex];
      if (!stepData) return;

      navigationLock.current = true;
      maybeNavigate(stepData);
      syncWizardStep(stepData.wizardStep);
      finalizeStep(stepIndex);
    },
    [navigate, dispatch, currentStep]
  );

  const handleNextStep = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (navigationLock.current || navigating) return;

      const nextStepIndex = currentStep + 1;
      if (nextStepIndex >= tutorialSteps.length) {
        dispatch(stopTutorial());
        return;
      }

      dispatch(setDirection("next"));
      dispatch(setIsLoading(true));
      processStep(nextStepIndex);
    },
    [currentStep, navigating, processStep]
  );

  const handlePrevStep = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (navigationLock.current || navigating) return;

      let targetStep = currentStep - 1;
      if (targetStep < 0) return;

      if (currentStep === 55 && targetStep === 54) {
        console.log("CURRENT STEP IS 55");
        targetStep = 47;
      }

      dispatch(setDirection("prev"));
      dispatch(setIsLoading(true));

      if (currentStep === 12 && targetStep === 11) {
        dispatch(openWizard());
      }

      processStep(targetStep);
    },
    [currentStep, navigating, processStep]
  );

  return { processStep, handleNextStep, handlePrevStep };
};

export default useTutorialSteps;
